import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import hero from "./assets/OnPageSEO/on-page-seo-accountants-hero.png";
import value from "./assets/OnPageSEO/seo-for-accountancy-firms-page-elements.png";
import process from "./assets/OnPageSEO/on-page-seo-internal-linking.png";
import sixStep from "./assets/OnPageSEO/on-page-seo-six-step-checklist.png";
import phone from "./assets/OnPageSEO/attoli-free-seo-review-call.png";
import Footer from "./Footer";

const OnPageSEO = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli fixes the on page seo for accountants and accounting firms holding back your rankings. Book a free call to see what your pages need today."
        />
        <title>On-Page SEO For Accountants and Accounting Firms</title>
        <link
          rel="canonical"
          href="https://attoli.com/services/seo-for-accountants-and-accounting-firms"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title">
                On-Page SEO For Accountants And{" "}
                <span className="hero-accent">Accounting Firms</span>
              </h1>
              <p className="servicePage-description">
                On-page seo for accountants and accounting firms means fixing
                the parts of your website Google reads first. We rewrite your
                titles headers and page content so search engines understand
                exactly what your firm does and who you serve.
              </p>

              <ul className="trust-badges">
                <li>No lock in contracts</li>
                <li>Built only for accounting and bookkeeping firms</li>
                <li>Free 20 minute call before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book A Free SEO Review
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Webpage mockup with highlighted sections around the title headline and body text representing on page optimization"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              Why SEO For Accountancy Firms Starts On The Page Itself
            </h2>
            <p className="body-copy">
              Off page work like backlinks and citations only goes so far if the
              page itself sends the wrong signals. A vague title tag or a header
              that says nothing tells Google your page is about everything and
              nothing at the same time.
            </p>
            <p className="body-copy">
              Most accounting firm websites were written for people not for
              search engines. That sounds fine until you realize search engines
              decide who sees the page in the first place.
            </p>
            <p className="body-copy">
              A thin page with no clear structure ranks below a competitor who
              took the time to organize their content properly. It has nothing
              to do with how good your firm actually is.
            </p>
            <p className="body-copy body-copy-emphasis">
              The fix starts with the page itself. Not backlinks. The words and
              structure already sitting on your site.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Our SEO Agency For Accountants Actually Fixes
            </h2>
            <p className="body-copy">
              We at Attoli review and rewrite the on-page elements most
              accounting firms never think to check. Here is what that looks
              like in practice.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Title Tag Rewrites</h3>
                <p>
                  We rewrite your title tags to include the terms clients
                  actually search for. A clear title tag is one of the strongest
                  ranking signals on a page.
                </p>
              </div>
              <div className="value-card">
                <h3>Meta Description Optimization</h3>
                <p>
                  We write meta descriptions that explain your service and add
                  strong SEO keywords. A strong description turns a search
                  result into a visit.
                </p>
              </div>
              <div className="value-card">
                <h3>Header Structure Fixes</h3>
                <p>
                  We organize your headers so Google and visitors can scan your
                  page and understand it instantly.
                </p>
              </div>
              <div className="value-card">
                <h3>Content Review And Rewrites</h3>
                <p>
                  We review your existing content and rewrite the sections that
                  are thin vague or outdated. To help the pages start ranking on
                  search engines.
                </p>
              </div>
              <div className="value-card">
                <h3>Internal Linking</h3>
                <p>
                  We connect your pages together the right way so visitors and
                  search engines can find your other services easily.
                </p>
              </div>
              <div className="value-card">
                <h3>Results Tracking</h3>
                <p>
                  We track ranking changes and traffic every month so you can
                  see exactly what improved.
                </p>
              </div>
            </div>
            <img
              src={value}
              alt="Webpage with labeled arrows pointing to the title tag headline and meta description"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              How On Page SEO Connects To Local SEO For Financial Advisors
            </h2>
            <p className="body-copy">
              The same on-page principles that help an accounting firm rank also
              apply to local SEO for financial advisors and any local service
              business. Clear titles organized headers and connected pages are
              what search engines reward everywhere.
            </p>
            <img
              src={process}
              alt="Several webpage icons connected by lines representing internal links between pages on the same site"
              className="inline-image"
            />

            <p className="body-copy">
              We apply the same structure to every page on your site.
              Consistency across pages is what turns individual fixes into real
              ranking movement.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Our On Page SEO Process Works Step By Step
            </h2>

            <img
              src={sixStep}
              alt="Six step checklist for on page seo covering titles descriptions headers content links and tracking"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>What is on-page SEO exactly?</summary>
              <p>
                It is everything on your website itself that affects rankings.
                Title tags, meta descriptions, headers, content, and internal
                links all count.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>
                Will this replace my need for backlinks or local SEO?
              </summary>
              <p>
                No. On page SEO works alongside local SEO and backlink building.
                Think of it as the foundation the rest of your strategy stands
                on.
              </p>
            </details>

            <details className="faq-item">
              <summary>How long until I see results?</summary>
              <p>
                Most firms see ranking movement within 60 to 90 days once the
                page fixes are live. Results build steadily from there.
              </p>
            </details>
          </div>
        </section>
        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To Fix What Your Pages Are Telling Google?
            </h2>
            <p className="body-copy">
              Book a free 20 minute call. We will review your current pages and
              show you exactly what is holding your rankings back.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default OnPageSEO;
